import React from 'react';

import { TableCell } from '@bb-ui/react-library/dist/components/TableCell';

import {
    ButtonBase,
    Link,
    Tooltip,
} from '@bb-ui/react-library/dist/components';
import { Edit } from '@bb-ui/icons/dist/small/Edit';
import { Trash } from '@bb-ui/icons/dist/small';
import { useTranslation } from 'react-i18next';
import { useStyles } from './UserList.styles';
import { User } from '../../../../contexts/LicenseManagementContext/LicenseManagementContext.types';
import { isUserDeletable } from '../DeleteDialog/userIsDeletable';
import { useFeatureContext } from '../../../../contexts/FeatureContext';
import { FeatureName } from '../../../../featureFlags/Feature.types';

const EnabledAction = (props: {
    testId: string;
    doAction: () => void;
    ariaLabel?: string;
    iconElement: typeof Trash | typeof Edit;
}) => {
    const { testId, ariaLabel, doAction, iconElement } = props;
    const Icon = iconElement;
    const classes = useStyles();

    return (
        <Link
            data-testid={testId}
            onClick={doAction}
            className={classes.actionContainer}
        >
            <ButtonBase
                className={classes.actionIconWrapper}
                aria-label={ariaLabel}
            >
                <Icon className={classes.actionIcon} />
            </ButtonBase>
        </Link>
    );
};

const DisabledAction = (props: {
    testId: string;
    disabledTooltipText: string;
    iconElement: typeof Trash | typeof Edit;
}) => {
    const { testId, disabledTooltipText, iconElement } = props;
    const Icon = iconElement;
    const classes = useStyles();

    return (
        <div data-testid={testId} className={classes.actionContainer}>
            <div className={classes.actionIconWrapper}>
                <Tooltip
                    title={disabledTooltipText}
                    placement="top"
                    role="tooltip"
                    arrow
                >
                    <Icon className={classes.actionIconDisabled} />
                </Tooltip>
            </div>
        </div>
    );
};

export const UserListTableActions = (props: {
    user: User;
    onEditButtonClick: () => void;
    onDeleteButtonClick: React.Dispatch<React.SetStateAction<User | undefined>>;
    sessionUserId: string | undefined;
}) => {
    const {
        user: u,
        onDeleteButtonClick: setUserToDelete,
        onEditButtonClick: redirectToUserList,
        sessionUserId: selfId,
    } = props;
    const classes = useStyles();
    const { t } = useTranslation();
    const rmEnabled =
        useFeatureContext()[
            'bbdata.roles.permissions.user.role.management' as FeatureName
        ];

    const [userCanBeDeleted, deleteDisabledReason] = React.useMemo(
        () => isUserDeletable(u, selfId, t, rmEnabled),
        [u, selfId, t, rmEnabled],
    );

    return (
        <TableCell role="gridcell">
            <div className={classes.actionsContainer}>
                <EnabledAction
                    testId={`edit-user-link-${u.userId}`}
                    doAction={redirectToUserList}
                    ariaLabel={t(
                        'settings.licenseManagement.editUserButtonAria',
                        {
                            username: u.userName,
                        },
                    )}
                    iconElement={Edit}
                />
                {userCanBeDeleted ? (
                    <EnabledAction
                        testId={`delete-user-link-${u.userId}`}
                        doAction={() => setUserToDelete(u)}
                        ariaLabel={t(
                            'settings.licenseManagement.deleteUserButtonAria',
                            {
                                username: u.userName,
                            },
                        )}
                        iconElement={Trash}
                    />
                ) : (
                    <DisabledAction
                        testId={`delete-user-disabled-${u.userId}`}
                        disabledTooltipText={deleteDisabledReason}
                        iconElement={Trash}
                    />
                )}
            </div>
        </TableCell>
    );
};
